import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles
} from "@material-ui/core";
import { Close as CloseIcon } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { Flaticon } from "../../components";
import PresentationsList from "../../components/PresentationsList";
import { createFavourite } from "../../ducks/actions";
import { getSession } from "../../ducks/actions/sessions";
import * as types from "../../ducks/types";
import { dateTimeInterval } from "../../ducks/utils";
import { useActionRequest } from "../../hooks";
import useSyncInterval from "../../hooks/useSyncInterval";
import { RecordingList, SponsoredSession } from "../sessions";

const useStyles = makeStyles(theme => ({
  liveNow: {
    color: "#FF0033",
    textTransform: "uppercase"
  },
  darkRow: {
    borderRadius: theme.spacing(1),
    backgroundColor: "rgba(0, 0, 0, 0.05)"
  }
}));

const SessionDialog = ({ id, onClose }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const classes = useStyles();

  useEffect(() => {
    dispatch(getSession({ id }));
  }, [dispatch, id]);

  const [isPolling, setIsPolling] = useState(false);
  useSyncInterval(() => {
    setIsPolling(true);
    dispatch(getSession({ id }));
  });

  const {
    data: { data: session },
    loading
  } = useQuery({ type: types.GET_SESSION, defaultData: { data: {} } });

  const createFavouriteRequest = useActionRequest({ action: createFavourite });

  const onCreateFavouriteProgramEvent = () => {
    createFavouriteRequest({
      favouritableId: session.programEvent?.id,
      favouritableType: "ProgramEvent"
    });
  };

  const onCreateFavouriteSession = () => {
    createFavouriteRequest({
      favouritableId: session.id,
      favouritableType: "Session"
    });
  };

  const isPast = moment(session.programEvent?.endAt).add(30, "minutes").isBefore();
  const isLive =
    !isPast && moment(session.programEvent?.startAt).subtract(30, "minutes").isBefore();
  return (
    <Dialog open={Boolean(id)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent>
        {loading && !isPolling ? (
          <LinearProgress />
        ) : (
          <>
            <Grid container justify="flex-end">
              {isPast && (
                <IconButton disabled={session.isFavourite} onClick={onCreateFavouriteSession}>
                  <Flaticon icon="star" />
                </IconButton>
              )}
              {!isPast && !isLive && (
                <IconButton
                  disabled={session.programEvent?.isFavourite}
                  onClick={onCreateFavouriteProgramEvent}
                >
                  <Flaticon icon="calendar-1" />
                </IconButton>
              )}
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Grid>

            <SponsoredSession session={session} />

            <List>
              <ListItem>
                <ListItemIcon>
                  <Flaticon fontSize="1.5rem" icon="calendar" />
                </ListItemIcon>
                <ListItemText
                  primary={<strong>{session.title}</strong>}
                  primaryTypographyProps={{ variant: "h4" }}
                  secondary={
                    <>
                      {intl.formatMessage(
                        { id: "Program.SessionDialog.Room", defaultMessage: "Room {rooms}" },
                        { rooms: session.programEvent?.roomList.join(", ") }
                      )}
                      &nbsp;&#8226;&nbsp;
                      {isLive ? (
                        <strong className={classes.liveNow}>
                          {intl.formatMessage({
                            id: "Program.ProgramSlot.LiveNow",
                            defaultMessage: "Live now"
                          })}
                        </strong>
                      ) : (
                        dateTimeInterval(session.programEvent?.startAt, session.programEvent?.endAt)
                      )}
                    </>
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemIcon />
                <ListItemText primary={session.description} />
              </ListItem>

              <PresentationsList presentations={session?.sessionPresentations} />

              {isPast && (
                <ListItem>
                  <ListItemIcon>
                    <Flaticon fontSize="1.5rem" icon="video-camera" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <>
                        <strong>
                          {intl.formatMessage({
                            id: "Program.SessionDialog.Recordings",
                            defaultMessage: "Recordings"
                          })}
                        </strong>
                        &nbsp;&#8226;&nbsp;
                        <Link component={RouterLink} to={`/sessions/${session.id}`}>
                          {intl.formatMessage({
                            id: "Program.SessionDialog.ViewAll",
                            defaultMessage: "View all"
                          })}
                        </Link>
                      </>
                    }
                  />
                </ListItem>
              )}
              {isPast && (
                <ListItem>
                  <ListItemIcon />
                  <RecordingList recordings={session.recordings} />
                </ListItem>
              )}
              {!isEmpty(session.speakers) && (
                <ListItem>
                  <ListItemIcon>
                    <Flaticon fontSize="1.5rem" icon="seminar" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <strong>
                        {intl.formatMessage({
                          id: "Program.SessionDialog.Speakers",
                          defaultMessage: "Speakers"
                        })}
                      </strong>
                    }
                    secondary={session.speakers
                      ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
                      .join(", ")}
                  />
                </ListItem>
              )}
              {!isEmpty(session.moderators) && (
                <ListItem>
                  <ListItemIcon>
                    <Flaticon fontSize="1.5rem" icon="user" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <strong>
                        {intl.formatMessage({
                          id: "Program.SessionDialog.Moderators",
                          defaultMessage: "Moderators"
                        })}
                      </strong>
                    }
                    secondary={session.moderators
                      ?.map(({ firstName, lastName }) => `${firstName} ${lastName}`)
                      .join(", ")}
                  />
                </ListItem>
              )}
            </List>
          </>
        )}
      </DialogContent>
      {isLive && (
        <>
          <Divider />
          <DialogActions>
            <Grid container justify="flex-end">
              <Button component={RouterLink} to={`/sessions/${session.id}`} color="primary">
                {intl.formatMessage({
                  id: "Program.SessionDialog.JoinRoom",
                  defaultMessage: "Join Room"
                })}
              </Button>
            </Grid>
          </DialogActions>
        </>
      )}
      {(isPast || (isLive && !session.zoomWebinarJoinUrl)) && <DialogActions />}
      {!isLive && !isPast && (
        <>
          <Divider />
          <DialogActions>
            <Grid container justify="flex-end">
              <Button
                disabled={session.programEvent?.isFavourite}
                color="primary"
                onClick={onCreateFavouriteProgramEvent}
              >
                {intl.formatMessage({
                  id: "Program.SessionDialog.AddToMyAgenda",
                  defaultMessage: "Add to my agenda"
                })}
              </Button>
            </Grid>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default SessionDialog;
